import { z } from "zod";
/**
 * Eventos que o servico de pagamentos emite pro backend da marca
 * (spec 000, secao 7.3). Entrega por HTTP assinado (HMAC-SHA256 de
 * `${timestamp}.${corpo}` com o segredo do ambiente), com outbox e retry.
 * O backend responde 200 so depois de processar de forma idempotente por `id`.
 */
export const Comprador = z.object({
    cpf: z.string().min(11).max(14),
    email: z.string().email(),
    nome: z.string().min(1),
    telefone: z.string().optional(),
    /**
     * A pessoa logada que comprou (spec 045): o nucleo anexa a compra a ela, sem procurar pelo CPF. Ausente nas compras de
     * antes da 045 e nas que nao passam pelo checkout logado (cobranca avulsa, Guru).
     */
    pessoa_id: z.string().min(1).optional(),
});
/**
 * Prova de posse do pagamento, usada pelo nucleo na recuperacao de e-mail (spec 001, 4.4):
 * PIX pelo E2E; cartao pelo final + valor. Vem da Pagar.me e nunca inclui o numero inteiro do cartao.
 */
export const Comprovante = z.object({
    metodo: z.enum(["pix", "cartao"]),
    pix_e2e: z.string().min(20).max(40).optional(),
    cartao_final: z.string().regex(/^\d{4}$/).optional(),
    cartao_bandeira: z.string().optional(),
    valor_centavos: z.number().int().nonnegative(),
    moeda: z.enum(["BRL", "USD"]),
    pago_em: z.string().datetime(),
});
export const Pedido = z.object({
    codigo: z.string(),
    /** O que a pessoa pagou, ja com o desconto e os juros. */
    valor_centavos: z.number().int().nonnegative(),
    moeda: z.enum(["BRL", "USD"]),
    metodo: z.enum(["pix", "cartao"]),
    parcelas: z.number().int().positive().default(1),
    /** Cupom de desconto usado na compra (spec 016). Ausente quando nao houve. */
    cupom: z.string().optional(),
    /** Promocao que deu o desconto (spec 067), pelo nome. Ausente quando nao houve. Nunca junto com o cupom. */
    promocao: z.string().optional(),
    desconto_centavos: z.number().int().nonnegative().optional(),
    /** Quando o pedido nasceu (o clique em pagar). O servico da Guru manda como `ordered_at` (spec 063). */
    criado_em: z.string().datetime().optional(),
});
/**
 * Item do pedido (spec 004). `plano_slug` presente = o item e um plano e vira matricula no backend.
 * Order bump que nao e plano vem sem `plano_slug` e nao vira matricula.
 * `beneficio` (spec 013, item 2): o que a oferta prometeu. `ENTREGA` (reset, day off) a equipe entrega e marca;
 * `CONDICAO` (100% de repasse, margem turbinada) vale la na frente e fica em vigor, sem pendencia; `PLANO_EM_DOBRO`
 * ("compre 1 leve 2") vira uma segunda matricula do plano principal, que o backend cria. Cupom e campanha continuam fora.
 */
export const EfeitoDoBeneficio = z.enum(["ENTREGA", "CONDICAO", "PLANO_EM_DOBRO"]);
export const ItemPedido = z.object({
    tipo: z.enum(["principal", "order_bump", "beneficio"]),
    /** `duracao_dias`: beneficio temporario, vale por tantos dias a partir do inicio do plano; sem ele, permanente. */
    beneficio: z.object({ id: z.string().min(1), efeito: EfeitoDoBeneficio, descricao: z.string().optional(), duracao_dias: z.number().int().positive().optional() }).optional(),
    plano_slug: z.string().min(1).optional(),
    nome: z.string(),
    valor_centavos: z.number().int().nonnegative(),
    /** Plataforma da oferta (Profit One ou Pro), quando o plano opera na Nelogica. A conta nasce nela (spec 007). */
    plataforma: z.enum(["PROFIT_ONE", "PROFIT_PRO"]).optional(),
});
export const Assinatura = z.object({
    id: z.string(),
    plano_slug: z.string(),
    ciclo_dias: z.number().int().positive(),
    carencia_dias: z.number().int().nonnegative(),
});
export const Fatura = z.object({
    id: z.string(),
    valor_centavos: z.number().int().nonnegative(),
    vence_em: z.string().datetime().optional(),
    pago_em: z.string().datetime().optional(),
    /** Presente quando a fatura foi paga: cada renovacao tambem serve de prova de posse. */
    comprovante: Comprovante.optional(),
});
const base = { id: z.string().uuid(), ocorrido_em: z.string().datetime() };
/**
 * O aceite do regulamento no checkout (spec 042): a versao (grupo) que a pagina mostrou e a hora do clique. O nucleo grava
 * um aceite por plano comprado, com os documentos que valem pra familia dele.
 */
export const AceiteDoRegulamento = z.object({
    grupo: z.number().int().positive(),
    aceito_em: z.string().datetime(),
    ip: z.string().max(80).optional(),
    navegador: z.string().max(300).optional(),
});
export const EventoPagamento = z.discriminatedUnion("tipo", [
    z.object({ ...base, tipo: z.literal("pagamento.confirmado"), pedido: Pedido, itens: z.array(ItemPedido).min(1), comprador: Comprador, comprovante: Comprovante, aceites: z.array(z.string()).default([]), aceite_regulamento: AceiteDoRegulamento.optional() }),
    z.object({ ...base, tipo: z.literal("pagamento.recusado"), pedido: Pedido, motivo: z.string() }),
    z.object({ ...base, tipo: z.literal("pagamento.expirado"), pedido: Pedido }),
    /** Dinheiro devolvido (total ou parcial). O backend avisa os operadores; quem decide encerrar e uma pessoa (spec 004, 5.4). */
    z.object({ ...base, tipo: z.literal("pagamento.reembolsado"), pedido: Pedido, valor_centavos: z.number().int().nonnegative(), parcial: z.boolean(), motivo: z.string().optional() }),
    /** Chargeback aberto pelo banco do comprador. Mesmo tratamento do reembolso: aviso e decisao humana. */
    z.object({ ...base, tipo: z.literal("pagamento.contestado"), pedido: Pedido, valor_centavos: z.number().int().nonnegative(), motivo: z.string().optional() }),
    /**
     * Cobranca avulsa paga (spec 003, 4.3): mensalidade ou portao do avanco. `referencia` e a que o backend deu ao pedir a
     * cobranca (`mensalidade:<id>`, `solicitacao:<id>`); e por ela que o backend sabe o que dar baixa.
     */
    z.object({ ...base, tipo: z.literal("cobranca.paga"), cobranca: z.object({ referencia: z.string().min(3), token: z.string() }), pedido: Pedido, comprador: Comprador, comprovante: Comprovante }),
    z.object({ ...base, tipo: z.literal("assinatura.ativada"), assinatura: Assinatura, comprador: Comprador }),
    z.object({ ...base, tipo: z.literal("assinatura.renovada"), assinatura: Assinatura, fatura: Fatura, comprador: Comprador }),
    z.object({ ...base, tipo: z.literal("assinatura.atrasada"), assinatura: Assinatura, fatura: Fatura, carencia_ate: z.string().datetime() }),
    z.object({ ...base, tipo: z.literal("assinatura.suspensa"), assinatura: Assinatura }),
    z.object({ ...base, tipo: z.literal("assinatura.cancelada"), assinatura: Assinatura, motivo: z.string() }),
    z.object({ ...base, tipo: z.literal("taxa_ativacao.paga"), assinatura: Assinatura, valor_centavos: z.number().int().nonnegative() }),
]);
/** Cabecalhos da entrega. */
export const CABECALHO_EVENTO_ID = "x-evento-id";
export const CABECALHO_EVENTO_TIMESTAMP = "x-evento-timestamp";
export const CABECALHO_EVENTO_ASSINATURA = "x-evento-assinatura";
//# sourceMappingURL=eventos-pagamento.js.map